// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  RootTagsManagementState,
  TagEntity,
} from '@shared/data-access/interfaces/redux/tags-management-state-types';
import { createTypedSelector } from '@shared/data-access/utils';
import {
  tagsAdapter,
  assignmentsAdapter,
  generateAssignmentId,
} from './tags-management-state';

const createSelector = createTypedSelector<RootTagsManagementState>();

const tagsSelectors = tagsAdapter.getSelectors();
const assignmentsSelectors = assignmentsAdapter.getSelectors();

export const selectTagsManagementState = (state: RootTagsManagementState) =>
  state.tagsManagement;

export const selectAllTags = createSelector(selectTagsManagementState, ({ tags }) =>
  tagsSelectors.selectAll(tags),
);

export const selectTagsEntities = createSelector(
  selectTagsManagementState,
  ({ tags }) => tags.entities,
);

export const selectTagsAssignments = createSelector(
  selectTagsManagementState,
  ({ assignments }) => assignmentsSelectors.selectAll(assignments),
);

export const selectAssignmentByObject = createSelector(
  [
    selectTagsManagementState,
    (_state: RootTagsManagementState, objectType: string, objectId: string) =>
      generateAssignmentId(objectType, objectId),
  ],
  (sliceState, assignmentId) =>
    assignmentsSelectors.selectById(sliceState.assignments, assignmentId),
);

export const selectTagsForObject = createSelector(
  [
    selectTagsManagementState,
    (_state: RootTagsManagementState, objectType: string) => objectType,
    (_state: RootTagsManagementState, _ot: string, objectId: string) => objectId,
  ],
  (sliceState, objectType, objectId): TagEntity[] => {
    const id = generateAssignmentId(objectType, objectId);
    const assignment = assignmentsSelectors.selectById(sliceState.assignments, id);

    if (!assignment || assignment.tag_ids.length === 0) return [];

    const { entities } = sliceState.tags;

    return assignment.tag_ids
      .map(tagId => entities[tagId as keyof typeof entities])
      .filter((t): t is TagEntity => t !== null);
  },
);
